// Navigation des fronts React, avec la forme de l'API next/link et
// next/navigation que les composants utilisaient déjà : `Link href`,
// `useRouter()` (push, replace, back, refresh) et `usePathname()`.
// `refresh()` recharge les données affichées (requêtes TanStack Query), le
// rôle qu'avait router.refresh() avec les composants serveur.
import { useQueryClient } from "@tanstack/react-query";
import { forwardRef, useMemo, type AnchorHTMLAttributes } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router";

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  href: string;
  prefetch?: boolean;
  replace?: boolean;
  scroll?: boolean;
};

function isExternal(href: string) {
  return /^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith("//");
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { href, prefetch: _prefetch, scroll: _scroll, replace, ...props },
  ref,
) {
  if (isExternal(href) || href.startsWith("#")) return <a href={href} ref={ref} {...props} />;
  return <RouterLink ref={ref} replace={replace} to={href} {...props} />;
});

export function useRouter() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMemo(
    () => ({
      push: (href: string) => (isExternal(href) ? window.location.assign(href) : navigate(href)),
      replace: (href: string) => (isExternal(href) ? window.location.replace(href) : navigate(href, { replace: true })),
      back: () => navigate(-1),
      refresh: () => void queryClient.invalidateQueries(),
    }),
    [navigate, queryClient],
  );
}

export function usePathname() {
  return useLocation().pathname;
}
